import { useEffect, useState } from 'react';
import { REPORT_NOTE_MAX, type ReportReason, type ReportTargetKind } from '@fewerbags/schema';
import { Alert } from './Alert';
import { Button } from './Button';
import { Dialog } from './Dialog';
import { SelectField } from './SelectField';
import { messageForError } from '../lib/errors';
import { normaliseNote, reasonOptionsFor, reportSentMessage } from '../lib/reports';

/**
 * One report dialog for shared photos, public recipes and shared
 * prices/locations. `send` performs the API call; who reported is never shown
 * to anyone.
 */
export function ReportDialog({
  open,
  onClose,
  kind,
  title,
  description,
  choices,
  send,
}: {
  open: boolean;
  onClose: () => void;
  kind: ReportTargetKind;
  title: string;
  description?: string | undefined;
  /** Optional "which value" picker (e.g. price or location); its value goes to `send`. */
  choices?: { value: string; label: string }[] | undefined;
  send: (input: {
    reason: ReportReason;
    note?: string;
    choice?: string;
  }) => Promise<{ hidden: boolean }>;
}) {
  const options = reasonOptionsFor(kind);
  const [choice, setChoice] = useState<string | undefined>(choices?.[0]?.value);
  const [reason, setReason] = useState<ReportReason>(options[0]!.value);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setReason(reasonOptionsFor(kind)[0]!.value);
      setChoice(choices?.[0]?.value);
      setNote('');
      setError(null);
      setDone(null);
    }
    // `choices` is read only when the dialog opens.
  }, [open, kind]);

  async function submit() {
    const n = normaliseNote(note);
    if (n === null) {
      setError(`Keep the note under ${REPORT_NOTE_MAX} characters.`);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await send({
        reason,
        ...(n === undefined ? {} : { note: n }),
        ...(choice === undefined ? {} : { choice }),
      });
      setDone(reportSentMessage(kind, res.hidden));
    } catch (err) {
      setError(messageForError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} title={title}>
      {done ? (
        <>
          <Alert tone="success">{done}</Alert>
          <Button onClick={onClose}>Close</Button>
        </>
      ) : (
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          {description ? <p className="text-sm text-muted">{description}</p> : null}
          {choices && choices.length > 1 ? (
            <SelectField
              id="report-choice"
              label="Which one?"
              value={choice}
              onChange={(e) => setChoice(e.target.value)}
              options={choices}
            />
          ) : null}
          <SelectField
            id="report-reason"
            label="What is wrong?"
            value={reason}
            onChange={(e) => setReason(e.target.value as ReportReason)}
            options={options}
          />
          <div className="flex flex-col gap-1">
            <label htmlFor="report-note" className="text-sm font-medium text-ink">
              Note (optional)
            </label>
            <textarea
              id="report-note"
              rows={3}
              maxLength={REPORT_NOTE_MAX}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="rounded-lg border border-line bg-surface px-3 py-2 text-base text-ink"
            />
          </div>
          <p className="text-xs text-muted">
            Reports are anonymous. Other households never see who reported.
          </p>
          {error ? <Alert tone="error">{error}</Alert> : null}
          <div className="flex gap-2">
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              {busy ? 'Sending…' : 'Send report'}
            </Button>
          </div>
        </form>
      )}
    </Dialog>
  );
}
